// start point for create activity
// start activity -> video demo -> create new activity -> customize activity -> assign activity
// using modal to control the flow of create activity
// 1. start activity -  simple card with next button
// 2. video demo - video demo with next button ( have a check box to not show this again)
// 3. create new activity - 3 options (magic suggestion, activity library, blank) -
// select activity saved in activity context -> selectedActivity
// 4. customize activity - customize selected activity
// 5. assign activity - assign selected activity ( 3 types of assign - custom, one time, repeat)
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import VideoDemo from './VideoDemo';
import CustomModel from '../Common/CustomModal';
import StartActivityCard from './StartActivityCard';
import SelectActivityTemplate from './CreateNewActivity';
import CustomizeActivityTemplate from './CustomizeActivityContainer/CustomizeActivityContainer';
import AssignActivityContainer from '../AssignActivityContainer/AssignActivityContainer';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchActivitiesRequest } from '../../redux/activity/activitySlice';

// for client activity table use this component
import ClientActivityTable from '../ClientActivitiesContainer/ActivityTable/ClientActivityTable';
import { getShowVideoDemoStatus } from '../../utils/utils';

function CreateActivityContainer({ initialStep = 0 }: { initialStep: number }) {
  const { id } = useParams<{ id: string }>();
  const [isModalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(initialStep);
  const dispatch = useAppDispatch();

  const coach = useAppSelector((state) => state.coach.coach);
  const coachId = coach?.id || null;
  const { isActivityCreated } = useAppSelector((state) => state.activity);
  const fetchActivitiesLoading = useAppSelector((state) => state.activity.fetchActivitiesLoading);

  const getActivitiesRequest = (_clientId: string, _coachId: string) => {
    dispatch(
      fetchActivitiesRequest({
        clientId: _clientId,
        coachId: _coachId,
        skip: 0,
        take: 10,
        filterText: '',
      })
    );
  };

  useEffect(() => {
    if (!id || !coachId) return;
    getActivitiesRequest(id, coachId);
  }, []);

  useEffect(() => {
    if (!id || !coachId) return;
    getActivitiesRequest(id, coachId);
  }, [isModalOpen]);

  useEffect(() => {
    if (step === 1 || step === 2) {
      setModalOpen(true);
    }
  }, [step]);

  const handleNext = () => {
    if (step === 0) {
      setStep((prevStep) => prevStep + 2);
      return;
    }
    setStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const closeModel = () => {
    setModalOpen(false);
    setStep(0);
  };

  // for client activity table
  const handleNewActivityCreation = () => {
    const shouldShowVideoDemo = getShowVideoDemoStatus();
    if (shouldShowVideoDemo === 'true') {
      setStep(2);
      return;
    }
    setStep(2);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      data-testid="outside-modal"
    >
      {/* in future this only renders if  clientActivities.length ===0 */}
      {step === 0 && !fetchActivitiesLoading && !isActivityCreated && (
        <StartActivityCard handleNext={handleNext} />
      )}
      {/* client activity table here */}
      {step === 0 && (isActivityCreated || fetchActivitiesLoading) && (
        <Box width="100%">
          <ClientActivityTable handleNewActivityCreation={handleNewActivityCreation} />
        </Box>
      )}

      {step === 1 && (
        <CustomModel isOpen={isModalOpen} xs={6.6}>
          <VideoDemo closeModel={closeModel} handleNext={handleNext} />
        </CustomModel>
      )}
      {step === 2 && (
        <CustomModel isOpen={isModalOpen} xs={11}>
          <SelectActivityTemplate closeModel={closeModel} handleNext={handleNext} />
        </CustomModel>
      )}
      {step === 3 && (
        <CustomModel isOpen={isModalOpen} xs={11}>
          <CustomizeActivityTemplate
            handleNext={handleNext}
            closeModel={closeModel}
            handleBack={handleBack}
          />
        </CustomModel>
      )}
      {step === 4 && (
        <CustomModel isOpen={isModalOpen} xs={5}>
          <AssignActivityContainer handleBack={handleBack} closeModel={closeModel} />
        </CustomModel>
      )}
    </Box>
  );
}

export default CreateActivityContainer;
